import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  Badge,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Dashboard,
  People,
  Gavel,
  Analytics,
  AccountCircle,
  Logout,
  AdminPanelSettings,
  Assessment,
  Checkroom
} from '@mui/icons-material';
import AdminDashboard from '../components/AdminDashboard';
import UserManagement from '../components/UserManagement';
import DisputeManagement from '../components/DisputeManagement';
import OrphanDressCleanup from '../components/OrphanDressCleanup';
import { useAuth } from '../contexts/AuthContext';
import { isUserAdmin } from '../utils/adminUtils';
import { signOut } from 'firebase/auth';
import { auth } from '../services/firebase/config';
import { localAuth } from '../services/localAuth';

const AdminScreen: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  // Vérification supplémentaire de sécurité
  useEffect(() => {
    const verifyAdmin = async () => {
      if (!user) {
        navigate('/login', { replace: true });
        return;
      }

      try {
        const adminStatus = await isUserAdmin(user.uid, user.email || undefined);
        setIsAdmin(adminStatus);
        
        if (!adminStatus) {
          // Rediriger si pas admin
          navigate('/dashboard', { replace: true, state: { error: 'Accès non autorisé à l\'administration' } });
        }
      } catch (error) {
        console.error('Erreur lors de la vérification admin:', error);
        navigate('/dashboard', { replace: true });
      } finally {
        setCheckingAdmin(false);
      }
    };

    verifyAdmin();
  }, [user, navigate]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      // Déconnexion Firebase
      await signOut(auth);
      // Déconnexion locale si utilisée
      await localAuth.signOut();
      // Fermer le menu
      handleMenuClose();
      // Rediriger vers la page de connexion
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
      // Même en cas d'erreur, rediriger vers login
      navigate('/login', { replace: true });
    }
  };

  const handleUserAction = (userId: string, action: string, data?: any) => {
    console.log('Action utilisateur:', { userId, action, data });
    // Ici on pourrait envoyer les données au serveur
  };

  const handleDisputeAction = (disputeId: string, action: string, data?: any) => {
    console.log('Action litige:', { disputeId, action, data });
    // Ici on pourrait envoyer les données au serveur
  };

  const tabs = [
    { label: 'Dashboard', icon: <Dashboard />, component: <AdminDashboard /> },
    { label: 'Utilisateurs', icon: <People />, component: <UserManagement onUserAction={handleUserAction} /> },
    { label: 'Litiges', icon: <Gavel />, component: <DisputeManagement onDisputeAction={handleDisputeAction} /> },
    { label: 'Annonces', icon: <Checkroom />, component: <OrphanDressCleanup /> },
    { label: 'Analytics', icon: <Analytics />, component: <AnalyticsTab /> }
  ];

  // Afficher un loader pendant la vérification
  if (checkingAdmin) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Si pas admin, ne rien afficher (la redirection est déjà gérée)
  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Accès non autorisé. Vous n'avez pas les privilèges administrateur.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* AppBar */}
      <AppBar position="static" sx={{ backgroundColor: '#a5816d' }}>
        <Toolbar>
          <AdminPanelSettings sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Administration Allure
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ mr: 2 }}>
              {user?.email}
            </Typography>
            <IconButton color="inherit" onClick={handleMenuOpen}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(255,255,255,0.2)' }}>
                <AccountCircle />
              </Avatar>
            </IconButton>
          </Box>
          
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              <ListItemText>Déconnexion</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Navigation tabs */}
      <Paper sx={{ borderRadius: 0 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              label={tab.label}
              icon={tab.icon}
              iconPosition="start"
              sx={{ 
                minHeight: 64,
                '&.Mui-selected': {
                  backgroundColor: '#f5f5f5'
                }
              }}
            />
          ))}
        </Tabs>
      </Paper>

      {/* Contenu principal */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {tabs[activeTab].component}
      </Box>
    </Box>
  );
};

// Composant pour l'onglet Analytics
const AnalyticsTab: React.FC = () => {
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ color: '#a5816d', mb: 3 }}>
        <Analytics sx={{ mr: 1, verticalAlign: 'middle' }} />
        Analytics et Statistiques
      </Typography>
      
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Assessment sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          Analytics en cours de développement
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Cette section contiendra des graphiques détaillés et des métriques avancées
        </Typography>
      </Box>
    </Box>
  );
};

export default AdminScreen;
